import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod/v4';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import {
  type IxfWatchlistV1OfacSdnQueryServiceOfacSdnListFilterableFields,
  IxfWatchlistV1OfacSdnQueryServiceOfacSdnListSearchableFields,
} from '@interxfi/types/generated/api';
import { PageTabRail } from '../../../../../../../../../../components/page-tab-rail/page-tab-rail';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../../../../../../components/router/router-tabbed-navigation';
import { OfacSdnsTable } from '../../../../../../../../../../features/watchlist/ofac/sdn/ofac-sdns-table';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';
import { tableSearchParamsSchema, useTableSearchParams } from '../../../../../../../../../../util/table-search-params';

const searchSchema = z.object({
  ...tableSearchParamsSchema.shape,
  searchField: z.enum(IxfWatchlistV1OfacSdnQueryServiceOfacSdnListSearchableFields).optional().catch(undefined),
});

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/compliance/_compliance/ofac/sdn/_sdn/')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.ClearingComplianceOfac,
      label: 'SDNs',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.ClearingComplianceOfac>,
  validateSearch: searchSchema,
  head: () => getPageHead('OFAC SDNs'),
  component: OfacSdnList,
});

function TableHeader({
  filter,
  search,
}: CustomTableHeaderProps<
  IxfWatchlistV1OfacSdnQueryServiceOfacSdnListFilterableFields,
  IxfWatchlistV1OfacSdnQueryServiceOfacSdnListSearchableFields
>) {
  return (
    <VerticalStack gap={7}>
      <PageTabRail
        actions={
          <VerticalStack gap={7}>
            <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
              {filter}
            </HorizontalStack>
          </VerticalStack>
        }
        group={TabbedNavigationGroup.ClearingComplianceOfac}
      />
      {search}
    </VerticalStack>
  );
}

function OfacSdnList() {
  const { initialSearchValue, initialSearchField } = useTableSearchParams<IxfWatchlistV1OfacSdnQueryServiceOfacSdnListSearchableFields>(
    Route.useSearch(),
  );

  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        <OfacSdnsTable
          HeaderActions={TableHeader}
          initialSearchFields={initialSearchField ? [initialSearchField] : undefined}
          initialSearchValue={initialSearchValue}
        />
      </PageLayout.Section>
    </PageLayout>
  );
}
